import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./UsersPage.scss";

import CreateSupplierComponent from "@components/CreateSupplierComponent/CreateSupplierComponent";
import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import CustomDialog from "@components/CustomDialog/CustomDialog";
import CustomHeader from "@components/CustomHeader/CustomHeader";
import CustomTable from "@components/CustomTable/CustomTable";
import Loader from "@components/Loader/Loader";
import { CreateModalContext } from "layouts/appLayout/layout";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";
import { useGetUsersByCompanyIdQuery } from "store/user/userApi";
import CreateUserComponent from "../CreateUserComponent/CreateUserComponent";
import HasPermission from "HasPermission/HasPermission";

const UsersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  const [openMenuId, setOpenMenuId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: users, isLoading: isLoadingUsers } =
    useGetUsersByCompanyIdQuery(companyId);

  const [userToDelete, setUserToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");

  const { toggleCreateModal } = useContext(CreateModalContext);

  const headers = [
    { key: "name", label: t("users.form.name") },
    { key: "email", label: t("users.form.email") },
    { key: "status", label: t("users.table.status") },
    { key: "createdAt", label: t("users.table.createdAt") },
  ];

  const renderCell = (key, user) => {
    if (key === "status") return user.status === "enabled" ? "✅" : "❌";
    if (key === "createdAt")
      return new Date(user.createdAt).toLocaleDateString();
    return user[key];
  };

  const handleEditUser = (user) => {
    setOpenMenuId(null);
    toggleCreateModal(
      <CreateUserComponent
        onClose={() => toggleCreateModal(<></>)}
        user={user}
        isUpdate={true}
      />,
      t("users.form.title_edit")
    );
  };

  const actionMenu = (user) => {
    const actions = [];

    // Edit User
    if (currentUser?.roleId?.permissions.includes("EDIT_USER")) {
      actions.push({
        label: t("users.actions.edit"),
        onClick: () => handleEditUser(user),
      });
    }

    return actions.length > 0 ? (
      <CustomActionMenu
        openMenuId={openMenuId}
        setOpenMenuId={setOpenMenuId}
        data={user}
        actions={actions}
      />
    ) : null;
  };

  // Vérification : est-ce qu'il existe au moins UNE permission action ?
  const hasActionPermission = ["EDIT_USER"].some((perm) =>
    currentUser?.roleId?.permissions.includes(perm)
  );

  const onSearchUser = (text) => {
    setSearchText(text.toLowerCase());
  };

  const filteredUsers = useMemo(() => {
    if (!users?.data) return [];
    if (searchText.length <= 1) return users.data;

    return users.data.filter((user) => {
      const name = user.name?.toLowerCase() || "";
      const email = user.email?.toLowerCase() || "";
      return name.includes(searchText) || email.includes(searchText);
    });
  }, [users?.data, searchText]);

  if (isLoadingUsers) return <Loader />;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("users.header_title")}
        dataLength={filteredUsers.length}
        onSearch={onSearchUser}
      />
      <CustomTable
        headers={headers}
        data={filteredUsers}
        rowKey="_id"
        renderCell={renderCell}
        // On ajoute la colonne action SEULEMENT si l'utilisateur a des permissions
        {...(hasActionPermission && { actionMenu })}
      />
    </div>
  );
};

export default UsersPage;
